import React, { MutableRefObject } from 'react';
import styled from 'styled-components';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import ErrorIcon from '@grnhse/seedling/lib/birch/icons/error';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import { PageName } from '../shared/types';
import GenerateKeywordsPopper from './generate_keywords_popper';
import Api from '../lib/api';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_search.form.sections.keywords');

interface Props {
  isLoading: boolean;
  error: string | null;
  displayedKeywords: Array<string>;
  hasKeywords: boolean;
  onAddItem: (keyword: string) => void;
  onAddAll: () => void;
  onRemoveAll: () => void;
  regenerateKeywords?: () => void;
  showRegenerateButton?: boolean;
  pageName: PageName;
  popperRef: MutableRefObject<HTMLElement | null>;
  apiClient?: Api;
  setAiKeywords?: (keywords: string[]) => void;
}

const ErrorBody = styled(Body)`
  display: flex;
  flex-direction: row;
  color: ${Tokens.colors.error};
`;

const SkeletonLoaderWrapper = styled.div`
  max-width: 400px;
`;

const StyledButton = styled(TertiaryButton)`
  &&&& {
    font-size: 0.875rem;
  }
`;

const VerticalDivider = styled.div`
  width: 2px;
  height: 10px;
  background-color: ${FullPalette.GREY_700};
  margin: 0 4px;
  display: inline-block;
`;

const AiKeywordsList = ({
  isLoading,
  error,
  displayedKeywords,
  hasKeywords,
  onAddItem,
  onAddAll,
  onRemoveAll,
  showRegenerateButton,
  regenerateKeywords,
  pageName,
  popperRef,
  apiClient,
  setAiKeywords,
}: Props) => {
  const showAiPopper = pageName === 'rediscovery';
  const showRetryButton = !showAiPopper && (showRegenerateButton || error);

  return (
    <Stack spacing={1} justifyContent="flex-start">
      {isLoading && (
        <SkeletonLoaderWrapper>
          <SkeletonLoader rows={1} columnsPerRow={1} />
        </SkeletonLoaderWrapper>
      )}

      {!isLoading && error ? (
        <ErrorBody>
          <ErrorIcon color={Tokens.colors.error} />
          {error}
        </ErrorBody>
      ) : null}

      {!isLoading && !error && displayedKeywords.length > 0 && (
        <div data-provides="suggestions-list">
          {displayedKeywords.map((keyword, i) => {
            return (
              <StyledButton
                onClick={() => onAddItem(keyword)}
                key={`${keyword}-${i}`}
              >
                {`${keyword}${i < displayedKeywords.length - 1 ? ', ' : ''}`}
              </StyledButton>
            );
          })}

          {!isLoading && !error && displayedKeywords.length > 0 && (
            <>
              <VerticalDivider />
              <StyledButton
                onClick={onAddAll}
                data-provides={'talent-search-add-all-suggestions-button'}
                size={'sm'}
              >
                {t('add_all_button')}
              </StyledButton>
            </>
          )}
        </div>
      )}
      {hasKeywords && displayedKeywords.length === 0 && (
        <div>
          <StyledButton
            data-provides="remove-suggestions-button"
            onClick={onRemoveAll}
            size={'sm'}
          >
            {t('clear_all_button')}
          </StyledButton>
        </div>
      )}

      {(showAiPopper || showRetryButton) && (
        <Stack direction="row" justifyContent="flex-start">
          {showAiPopper && (
            <GenerateKeywordsPopper
              hasAiKeywords={hasKeywords}
              anchorEl={popperRef}
              apiClient={apiClient as Api}
              setGeneratedKeywords={
                setAiKeywords as (keywords: string[]) => void
              }
            />
          )}

          {showRetryButton && (
            <Tooltip
              label={t('generate_button_tooltip')}
              ariaLabel={t('generate_button_tooltip')}
            >
              <SecondaryButton
                data-provides="regenerate-button"
                onClick={regenerateKeywords}
                disabled={isLoading}
                icon={AiMagicIcon}
                size={'sm'}
              >
                {t('generate_button')}
              </SecondaryButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default AiKeywordsList;
